import * as React from 'react'

import { ActionItem, Box, Icon, Text, useToggle } from '@factorialco/deprecated-design-system'

import ListEmployees from 'modules/core/components/ListEmployees'
import Popover from 'modules/core/components/Popover'
import ScrollableItem from 'modules/core/components/ScrollableItem'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { orderBy } from 'modules/core/lib/filters'
import FiltersForm from 'modules/core/lib/forms/Filters'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'

const getEmployeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = users.get(userId)
  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }
  return user
}

type ActionItemProps = React.ComponentProps<typeof ActionItem>

interface Props {
  employee: Employee
  employees: Employee[]
  goToEmployeeTimesheet: (employeeId: number) => void
}
interface ItemProps {
  employee: Employee
  onClick: ActionItemProps['onClick']
}

interface EmployeeItemProps {
  employee: Employee
  avatarSize: 's24' | 's32'
  textSize: '300' | '400'
}

const useSortedEmployees = (employees: Employee[], filtersForm: FiltersForm) => {
  const [orderedEmployees, setOrderedEmployees] = React.useState<{
    loading: boolean
    value: Employee[]
  }>({ loading: false, value: [] })

  React.useEffect(() => {
    const orderFn = async () => {
      setOrderedEmployees({
        loading: true,
        value: [],
      })

      const result = await orderBy(filtersForm, employees)

      setOrderedEmployees({
        loading: false,
        value: result,
      })
    }

    orderFn()
  }, [
    filtersForm.get('sortDirection').value,
    filtersForm.get('sortAttribute').value,
    filtersForm.get('filterBy').value,
    employees.length,
  ])

  return orderedEmployees
}

const EmployeeItem = ({ employee, avatarSize, textSize }: EmployeeItemProps) => {
  return (
    <Box flexDirection="row" alignItems="center" gap="s12">
      <UserAvatar user={getEmployeeUser(employee)} size={avatarSize} />
      <Text.Normal weight="semibold" size={textSize} ellipsis>
        {getFullDisplayName({
          firstName: getEmployeeUser(employee).attributes.first_name,
          lastName: getEmployeeUser(employee).attributes.last_name,
          canSharePreferredName: employee.policy.can('sharePreferredName'),
          preferredName: employee.attributes.preferred_name,
        })}
      </Text.Normal>
    </Box>
  )
}

const Item = ({ employee, onClick }: ItemProps) => {
  return (
    <ScrollableItem>
      <ActionItem onClick={onClick}>
        <EmployeeItem employee={employee} avatarSize="s24" textSize="300" />
      </ActionItem>
    </ScrollableItem>
  )
}

const EmployeeSelector = ({ employee, employees, goToEmployeeTimesheet }: Props) => {
  const [open, toggle] = useToggle(false)
  const [childPopoverOpen, setChildPopoverOpen] = React.useState(false)

  const filtersForm = useLazyRef(
    () =>
      new FiltersForm({
        sortAttribute: 'first_name',
        sortDirection: 'asc',
        filterBy: 'activity___active',
      })
  ).current

  const sortedEmployees = useSortedEmployees(employees, filtersForm)

  return (
    <Box flexDirection="row" alignItems="center" gap="s24">
      <Popover
        attachment="top center"
        open={open}
        toggle={toggle}
        size="bigger"
        disableOutsideClick={childPopoverOpen}
      >
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="flexStart"
          width="full"
          cursor="pointer"
          outline="none"
          gap="s12"
        >
          <EmployeeItem employee={employee} avatarSize="s32" textSize="400" />
          <Icon.Small icon="caret-double" color="grey800" />
        </Box>
        <Box padding="s24">
          <ListEmployees
            employees={sortedEmployees.value}
            filtersForm={filtersForm}
            setChildPopoverOpen={setChildPopoverOpen}
            renderEmployeeListItem={(employee) => (
              <Item
                key={employee.id}
                employee={employee}
                onClick={() => goToEmployeeTimesheet(employee.id)}
              />
            )}
            listSize="small"
          />
        </Box>
      </Popover>
    </Box>
  )
}

export default EmployeeSelector
